import { NgModule } from '@angular/core';
import { RouterModule, Routes, NoPreloading } from '@angular/router';

// Layout Components
import { SalaryLayoutComponent } from './features/salary/layout/salary-layout/salary-layout.component';
import { BudgetLayoutComponent } from './features/budget/layout/budget-layout/budget-layout.component';
import { CashLayoutComponent } from './features/cash/layout/cash-layout/cash-layout.component';
import { AccountingLayoutComponent } from './features/accounting/layout/accounting-layout/accounting-layout.component';
import { GrantsLayoutComponent } from './features/grants/layout/grants-layout/grants-layout.component';
import { AdvancesLayoutComponent } from './features/advances/layout/advances-layout/advances-layout.component';
import { CpfLayoutComponent } from './features/cpf/layout/cpf-layout/cpf-layout.component';
import { AssetsLayoutComponent } from './features/assets/layout/assets-layout/assets-layout.component';
import { AuditLayoutComponent } from './features/audit/layout/audit-layout/audit-layout.component';
import { AdminLayoutComponent } from './features/admin/layout/admin-layout/admin-layout.component';
import { VendorManagementLayoutComponent } from './features/vendor-management/layout/vendor-management-layout/vendor-management-layout.component';

// Guards
import { AuthGuard } from './core/guards/auth.guard';
import { PermissionGuard } from './core/guards/permission.guard';
import { actingPostGuard, actingPostFeatureGuard, actingPostReturnGuard } from './core/guards/acting-post.guard';
import { salaryCoaGuard } from './features/salary/salary-coa';

// NOTE: ModuleLayoutComponent is being phased out in favor of service-specific layouts
// For workflow (no sidebar), we might still use a simpler layout or create one.
// Since ModuleLayoutComponent was reported missing, we should handle workflow as well.
// For now, I'll use a generic approach or create WorkflowLayout if needed.

const routes: Routes = [
  {
    path: 'login',
    loadChildren: () =>
      import('./features/login/login.module').then((m) => m.LoginModule),
  },
  {
    path: 'login/posting',
    // Deliberately NOT actingPostGuard (that would infinite-loop: the guard
    // redirects here) and NOT PermissionGuard (no module context exists yet).
    canActivate: [AuthGuard, actingPostReturnGuard, actingPostFeatureGuard],
    loadComponent: () =>
      import('./features/login/posting-select/posting-select.component').then((m) => m.PostingSelectComponent),
  },
  {
    path: 'modules',
    canActivate: [AuthGuard, actingPostGuard],
    loadChildren: () =>
      import('./features/module-selector/module-selector.module').then((m) => m.ModuleSelectorModule),
  },

  // ── Feature modules — each with its own sidebar layout ──

  {
    path: 'salary',
    component: SalaryLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, PermissionGuard, salaryCoaGuard],
    data: { module: 'salary' },
    children: [
      {
        path: 'notifications',
        loadComponent: () => import('./shared/pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
        data: { hideSidebar: true },
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/salary/salary.module').then((m) => m.SalaryModule),
      }],
  },
  {
    path: 'budget',
    component: BudgetLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, PermissionGuard],
    data: { module: 'budget' },
    children: [
      {
        path: 'notifications',
        loadComponent: () => import('./shared/pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
        data: { hideSidebar: true },
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/budget/budget.module').then((m) => m.BudgetModule),
      }],
  },
  {
    path: 'cash',
    component: CashLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, PermissionGuard],
    data: { module: 'cash' },
    children: [
      {
        path: 'notifications',
        loadComponent: () => import('./shared/pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
        data: { hideSidebar: true },
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/cash/cash.module').then((m) => m.CashModule),
      }],
  },
  {
    path: 'accounting',
    component: AccountingLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, PermissionGuard],
    data: { module: 'accounting' },
    children: [
      {
        path: 'notifications',
        loadComponent: () => import('./shared/pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
        data: { hideSidebar: true },
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/accounting/accounting.module').then((m) => m.AccountingModule),
      }],
  },
  {
    path: 'grants',
    component: GrantsLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, PermissionGuard],
    data: { module: 'grants' },
    children: [
      {
        path: 'notifications',
        loadComponent: () => import('./shared/pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
        data: { hideSidebar: true },
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/grants/grants.module').then((m) => m.GrantsModule),
      }],
  },
  {
    path: 'advances',
    component: AdvancesLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, PermissionGuard],
    data: { module: 'advances' },
    children: [
      {
        path: 'notifications',
        loadComponent: () => import('./shared/pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
        data: { hideSidebar: true },
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/advances/advances.module').then((m) => m.AdvancesModule),
      }],
  },
  {
    path: 'cpf',
    component: CpfLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, PermissionGuard],
    data: { module: 'cpf' },
    children: [
      {
        path: 'notifications',
        loadComponent: () => import('./shared/pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
        data: { hideSidebar: true },
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/cpf/cpf.module').then((m) => m.CpfModule),
      }],
  },
  {
    path: 'assets',
    component: AssetsLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, PermissionGuard],
    data: { module: 'assets' },
    children: [
      {
        path: 'notifications',
        loadComponent: () => import('./shared/pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
        data: { hideSidebar: true },
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/assets/assets.module').then((m) => m.AssetsModule),
      }],
  },
  {
    path: 'audit',
    component: AuditLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, PermissionGuard],
    data: { module: 'audit' },
    children: [
      {
        path: 'notifications',
        loadComponent: () => import('./shared/pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
        data: { hideSidebar: true },
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/audit/audit.module').then((m) => m.AuditModule),
      }],
  },
  {
    path: 'admin',
    component: AdminLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, PermissionGuard],
    data: { module: 'admin' },
    children: [
      {
        path: 'notifications',
        loadComponent: () => import('./shared/pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
        data: { hideSidebar: true },
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/admin/admin.module').then((m) => m.AdminModule),
      }],
  },
  {
    path: 'vendor-management',
    component: VendorManagementLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, PermissionGuard, salaryCoaGuard],
    data: { module: 'vendor-management' },
    children: [
      {
        path: 'notifications',
        data: { hideSidebar: true },
        loadComponent: () =>
          import('./shared/pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
      },
      {
        path: 'vouchers',
        data: { hideSidebar: true },
        loadComponent: () =>
          import('./features/workflow/pages/master-voucher-list/master-voucher-list.component').then((m) => m.MasterVoucherListComponent),
      },
      {
        path: 'vouchers/details',
        data: { hideSidebar: true },
        loadComponent: () =>
          import('./features/workflow/pages/master-voucher/master-voucher.component').then((m) => m.MasterVoucherComponent),
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/vendor-management/vendor-management.module').then((m) => m.VendorManagementModule),
      },
    ],
  },

  // ── Workflow — no sidebar, accessible from navbar ──
  {
    path: 'workflow',
    canActivate: [AuthGuard, actingPostGuard, salaryCoaGuard],
    data: { moduleKey: '' },
    loadChildren: () =>
      import('./features/workflow/workflow.module').then((m) => m.WorkflowModule),
  },

  {
    path: 'notifications',
    component: SalaryLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard],
    data: { hideSidebar: true },
    children: [
      {
        path: '',
        loadComponent: () => import('./shared/pages/notifications/notifications.component').then(m => m.NotificationsComponent),
      }
    ]
  },

  // ── Logged-in user's own profile (reached from the header "View Profile" menu) ──
  {
    path: 'profile',
    component: SalaryLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard],
    data: { hideSidebar: true },
    children: [
      {
        path: '',
        loadComponent: () => import('./shared/pages/profile/profile.component').then(m => m.ProfileComponent),
      }
    ]
  },

  // ── Generic Voucher Master Dashboard ──
  {
    path: 'vouchers',
    component: SalaryLayoutComponent,
    canActivate: [AuthGuard, actingPostGuard, salaryCoaGuard],
    data: { hideSidebar: true },
    children: [
      {
        path: '',
        loadComponent: () => import('./features/workflow/pages/master-voucher-list/master-voucher-list.component').then(m => m.MasterVoucherListComponent),
      },
      {
        path: 'details',
        loadComponent: () => import('./features/workflow/pages/master-voucher/master-voucher.component').then(m => m.MasterVoucherComponent),
      }
    ]
  },

  // ── Error / Catch-all ──

  {
    path: 'unauthorized',
    loadComponent: () =>
      import('./shared/pages/unauthorized/unauthorized.component').then((m) => m.UnauthorizedComponent),
  },

  // Default: logged-in users go to modules, guests go to login
  { path: '', redirectTo: 'modules', pathMatch: 'full' },

  // Catch-all: unknown paths go to login (AuthGuard will redirect if needed)
  { path: '**', redirectTo: 'modules' }];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // Lazy chunks load when their route is opened: preloading all of them competes with the API calls right after login.
      preloadingStrategy: NoPreloading,
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      enableTracing: false,
      bindToComponentInputs: true,
    })],
  exports: [RouterModule],
})
export class AppRoutingModule { }
